import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { blogPosts } from '../data/blogData';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../styles/BlogPage.css';

const BlogPage = () => {
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // SEO: title, meta description, canonical, OG/Twitter tags, JSON-LD
  React.useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Marketing Insights & Growth Strategy Blog | Sanjib Roy';

    const upsertMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      let prev = null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      } else {
        prev = el.getAttribute('content');
      }
      el.setAttribute('content', content);
      return { el, prev };
    };

    const description = 'Data-driven insights on performance marketing, paid media, GA4, attribution, and AI-driven growth strategies from performance marketing consultant Sanjib Roy.';
    const canonicalHref = 'https://sanjib-roy.com/blog/';

    const descRes = upsertMeta('description', description);
    const ogTitleRes = upsertMeta('og:title', 'Marketing Insights & Growth Strategy Blog | Sanjib Roy', true);
    const ogDescRes = upsertMeta('og:description', description, true);
    const ogUrlRes = upsertMeta('og:url', canonicalHref, true);
    const ogTypeRes = upsertMeta('og:type', 'website', true);
    const twTitleRes = upsertMeta('twitter:title', 'Marketing Insights & Growth Strategy Blog | Sanjib Roy');
    const twDescRes = upsertMeta('twitter:description', description);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    let prevCanonicalHref = null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    } else {
      prevCanonicalHref = canonical.getAttribute('href');
    }
    canonical.setAttribute('href', canonicalHref);

    // Remove the global homepage FAQPage schema on this interior page (restore on unmount)
    const globalFaqEl = document.getElementById('global-faq-schema');
    const globalFaqContent = globalFaqEl ? globalFaqEl.textContent : null;
    if (globalFaqEl) globalFaqEl.remove();

    const restoreMeta = ({ el, prev }) => {
      if (el && prev !== null) el.setAttribute('content', prev);
    };

    return () => {
      document.title = prevTitle;
      restoreMeta(descRes);
      restoreMeta(ogTitleRes);
      restoreMeta(ogDescRes);
      restoreMeta(ogUrlRes);
      restoreMeta(ogTypeRes);
      restoreMeta(twTitleRes);
      restoreMeta(twDescRes);
      if (canonical && prevCanonicalHref !== null) canonical.setAttribute('href', prevCanonicalHref);
      if (globalFaqContent && !document.getElementById('global-faq-schema')) {
        const restored = document.createElement('script');
        restored.type = 'application/ld+json';
        restored.id = 'global-faq-schema';
        restored.text = globalFaqContent;
        document.head.appendChild(restored);
      }
    };
  }, []);

  return (
    <div className="blog-page">
      <Header />
      
      <section className="blog-hero">
        <div className="blog-hero-container">
          <h1 className="blog-hero-title">Marketing Insights & Strategies</h1>
          <p className="blog-hero-subtitle">
            Data-driven insights on performance marketing, AI integration, and technical infrastructure
          </p>
        </div>
      </section>

      <section className="blog-listing">
        <div className="blog-listing-container">
          <div className="blog-grid-large">
            {blogPosts.map((post) => (
              <article key={post.id} className="blog-card-large">
                <Link to={`/blog/${post.slug}/`} className="blog-card-link">
                  <div className="blog-image-wrapper-large">
                    <img src={post.image} alt={post.title} className="blog-image-large" />
                    <div className="blog-category-badge">{post.category}</div>
                  </div>
                  
                  <div className="blog-content-large">
                    <div className="blog-meta-large">
                      <span className="blog-meta-item">
                        <Calendar size={16} />
                        {post.date}
                      </span>
                      <span className="blog-meta-item">
                        <Clock size={16} />
                        {post.readTime}
                      </span>
                    </div>
                    
                    <h2 className="blog-title-large">{post.title}</h2>
                    <p className="blog-excerpt-large">{post.excerpt}</p>
                    
                    <div className="blog-read-more">
                      Read Full Article
                      <ArrowRight size={18} />
                    </div>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;
